/**
 * CrewPayPaidEmailModal — "Send crew pay paid email" composer.
 *
 * Opened from the Crew Pay or PM Pay pill of the Commission board. Shannon
 * picks a day, the modal pulls every roof_crew_payment marked paid that day
 * (grouped by vendor, default Risol), shows an editable summary email, and
 * sends it via the crew-pay-summary-email edge function. Mirrors the
 * collection-email edit-then-send UX.
 *
 * TWO SEPARATE EMAILS (per Greg, Oct 13 2026):
 *   - "Crew job payments" — the job money only. Ramiro's PM pay is NEVER on
 *     this email: PM rows are filtered out of the job list, there is no
 *     pre-seeded Ramiro line, and the intro does not mention him. Bardin
 *     sends Ramiro's money to Risol as its own ACH.
 *   - "Ramiro PM pay" — the separate payment. Lists the PM rows paid that day
 *     (biweekly base + any per-build fee rows) and tells Julian exactly what
 *     to pay Ramiro. Logged as payment_kind='pm_pay_summary'.
 *   A row is "PM pay" when it has no deal (deal_id IS NULL) or its description
 *   starts with the trigger markers "PM fee" / "PM biweekly" — matched on the
 *   marker, not the name, so a future PM retarget keeps working
 *   (`isPmPayRow`, lib/pmPay — the same test the two pills use).
 * Oct 20, 2026 — each pill opens its own email: Crew Pay → the job email,
 * PM Pay → the PM email (`kind` prop). There is no switch between the two
 * inside the modal any more.
 *
 * Extra lines on either email are for adjustments paid on top of (or withheld
 * from) the listed rows — e.g. a loan repayment deduction. Negative = deduction.
 *
 * CC defaults: Greg, Shannon, Chad.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import { fmtUSD } from "../../lib/analytics";
import { useAuth } from "../../context/AuthContext";
import { formatSupabaseError } from "../../lib/formatSupabaseError";
import { fetchPmName, isPmPayRow } from "../../lib/pmPay";

const DEFAULT_CC = ["greg@bardinroofing.com", "sser@bardinroofing.com", "chad@bardinroofing.com"];

type EmailKind = "crew" | "pm";

interface PaidRow {
  id: string;
  /** null for non-job rows such as the PM biweekly base. */
  deal_id: string | null;
  vendor_id: string;
  vendor_name: string;
  vendor_email: string | null;
  squares: number;
  total: number;
  additional_work_description: string | null;
  homeowner_name: string | null;
  property_street: string | null;
  property_city: string | null;
}

interface ExtraLine { label: string; amount: string; }

function localDayBounds(dateStr: string): { startISO: string; endISO: string } {
  // dateStr = "YYYY-MM-DD" in local time. Build local midnight → next midnight.
  const start = new Date(`${dateStr}T00:00:00`);
  const end = new Date(start.getTime() + 24 * 60 * 60 * 1000);
  return { startISO: start.toISOString(), endISO: end.toISOString() };
}

function todayLocalISODate(): string {
  const d = new Date();
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - off).toISOString().slice(0, 10);
}

export default function CrewPayPaidEmailModal({
  kind,
  open,
  onClose,
}: {
  /** Which email: the crew job money (Crew Pay pill) or the PM's pay (PM Pay pill). */
  kind: EmailKind;
  open: boolean;
  onClose: () => void;
}) {
  const { member } = useAuth();

  const [pmName, setPmName] = useState("Ramiro Perez");
  const [date, setDate] = useState<string>(todayLocalISODate());
  const [rows, setRows] = useState<PaidRow[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [vendorId, setVendorId] = useState<string | null>(null);

  const [include, setInclude] = useState<Record<string, boolean>>({});
  const [extraLines, setExtraLines] = useState<ExtraLine[]>([]);

  const [subject, setSubject] = useState("");
  const [intro, setIntro] = useState("");
  const [toEmail, setToEmail] = useState("");
  const [ccText, setCcText] = useState(DEFAULT_CC.join(", "));

  const [sending, setSending] = useState(false);
  const [sendErr, setSendErr] = useState<string | null>(null);
  const [sendOk, setSendOk] = useState<string | null>(null);

  useEffect(() => { if (open) fetchPmName().then(setPmName); }, [open]);

  const load = useCallback(async () => {
    setRows(null);
    setLoadErr(null);
    const { startISO, endISO } = localDayBounds(date);
    const { data, error } = await supabase
      .from("roof_crew_payments")
      .select(`
        id, deal_id, vendor_id, squares, total, additional_work_description,
        deals(homeowner_name, property_street, property_city),
        vendors!inner(name, primary_email)
      `)
      .eq("status", "paid")
      .gte("paid_at", startISO)
      .lt("paid_at", endISO)
      .order("paid_at", { ascending: true });
    if (error) { setLoadErr(error.message); setRows([]); return; }
    const list: PaidRow[] = (data ?? []).map((r: any) => ({
      id: r.id,
      deal_id: r.deal_id,
      vendor_id: r.vendor_id,
      vendor_name: r.vendors?.name ?? "Unknown vendor",
      vendor_email: r.vendors?.primary_email ?? null,
      squares: Number(r.squares ?? 0),
      total: Number(r.total ?? 0),
      additional_work_description: r.additional_work_description,
      homeowner_name: r.deals?.homeowner_name ?? null,
      property_street: r.deals?.property_street ?? null,
      property_city: r.deals?.property_city ?? null,
    }));
    setRows(list);
    setInclude(Object.fromEntries(list.map((r) => [r.id, true])));
    // Pick the vendor with the most paid rows that day.
    const counts: Record<string, number> = {};
    for (const r of list) counts[r.vendor_id] = (counts[r.vendor_id] ?? 0) + 1;
    const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
    setVendorId(top);
  }, [date]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const vendors = useMemo(() => {
    const m: Record<string, { id: string; name: string; email: string | null }> = {};
    for (const r of rows ?? []) {
      if (!m[r.vendor_id]) m[r.vendor_id] = { id: r.vendor_id, name: r.vendor_name, email: r.vendor_email };
    }
    return Object.values(m);
  }, [rows]);

  const vendorRows = useMemo(
    () => (rows ?? []).filter((r) => r.vendor_id === vendorId),
    [rows, vendorId],
  );

  // The two emails never share a row: crew = job rows only, pm = PM rows only.
  const kindRows = useMemo(
    () => vendorRows.filter((r) => isPmPayRow(r) === (kind === "pm")),
    [vendorRows, kind],
  );
  const pmRowCount = useMemo(() => vendorRows.filter(isPmPayRow).length, [vendorRows]);

  const selectedVendor = vendors.find((v) => v.id === vendorId) ?? null;

  // Keep recipient + subject/intro in sync when kind/vendor/date/rows change.
  useEffect(() => {
    if (selectedVendor) setToEmail(selectedVendor.email ?? "");
  }, [selectedVendor]);

  useEffect(() => {
    const included = kindRows.filter((r) => include[r.id]);
    const n = included.length;
    const d = new Date(`${date}T00:00:00`);
    const md = `${d.getMonth() + 1}/${d.getDate()}`;
    if (kind === "pm") {
      const amt = included.reduce((s, r) => s + r.total, 0);
      setSubject(`Bardin Roofing — ${pmName} PM Pay Sent ${md} (${fmtUSD(amt)})`);
      setIntro(
        `Hi,\n\nThis is ${pmName}'s PM pay, sent via ACH on ${md} as a separate payment from the crew job money. ` +
        `Please pay ${pmName} the amount below.`,
      );
    } else {
      setSubject(`Bardin Roofing — Crew Pay Sent ${md} (${n} job${n === 1 ? "" : "s"})`);
      setIntro(
        `Hi,\n\nHere is a summary of the crew payment sent via ACH on ${md}. ` +
        `Each job is squares × rate + flat fee.`,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, vendorId, date, rows, pmName]);

  // Extra lines belong to one email or the other; each pill mounts its own modal.
  useEffect(() => { setExtraLines([]); setSendErr(null); setSendOk(null); }, [kind]);

  const includedRows = kindRows.filter((r) => include[r.id]);
  const jobsSubtotal = includedRows.reduce((s, r) => s + r.total, 0);
  const extrasParsed = extraLines
    .map((e) => ({ label: e.label.trim(), amount: Number(e.amount) }))
    // Keep any nonzero line — negatives are deductions (e.g. a loan repayment
    // withheld from the crew pay) and MUST reduce the total sent.
    .filter((e) => e.label && Number.isFinite(e.amount) && e.amount !== 0);
  const extrasTotal = extrasParsed.reduce((s, e) => s + e.amount, 0);
  const grandTotal = jobsSubtotal + extrasTotal;

  function setExtra(i: number, patch: Partial<ExtraLine>) {
    setExtraLines((prev) => prev.map((e, idx) => (idx === i ? { ...e, ...patch } : e)));
  }
  function addExtra() { setExtraLines((prev) => [...prev, { label: "", amount: "" }]); }
  function removeExtra(i: number) { setExtraLines((prev) => prev.filter((_, idx) => idx !== i)); }

  async function send() {
    if (!toEmail.trim()) { setSendErr("Add a recipient email."); return; }
    if (!subject.trim()) { setSendErr("Add a subject."); return; }
    if (includedRows.length === 0 && extrasParsed.length === 0) {
      setSendErr("Nothing to send — include at least one row or extra line.");
      return;
    }
    setSending(true);
    setSendErr(null);
    setSendOk(null);
    try {
      const jobs = includedRows.map((r) => ({
        // PM email: the marker line is the name ("PM fee — Ramiro Perez: $100 …"),
        // and the job it was for sits underneath. Crew email: homeowner + address.
        name: kind === "pm"
          ? (r.additional_work_description ?? `${pmName} — PM pay`)
          : (r.homeowner_name ?? "(job)"),
        address: kind === "pm"
          ? (r.deal_id ? [r.homeowner_name, r.property_street, r.property_city].filter(Boolean).join(", ") : "")
          : ([r.property_street, r.property_city].filter(Boolean).join(", ") || "Not job-specific"),
        squares: kind === "pm" ? undefined : r.squares,
        amount: r.total,
        // PM rows are the whole point of the PM email — bold them so Julian can't miss the figure.
        highlight: kind === "pm",
      }));
      const cc = ccText.split(",").map((s) => s.trim()).filter(Boolean);
      const { data, error } = await supabase.functions.invoke("crew-pay-summary-email", {
        body: {
          kind,
          to: toEmail.trim(),
          cc,
          subject: subject.trim(),
          intro,
          jobs,
          extra_lines: extrasParsed.map((e) => ({ label: e.label, amount: e.amount, highlight: true })),
          payment_ids: includedRows.map((r) => r.id),
          vendor_id: vendorId,
          sender_id: member?.id ?? null,
        },
      });
      const serverErr = (data as { error?: string } | null)?.error;
      if (error || serverErr) throw new Error(serverErr ?? error?.message ?? "Send failed");
      setSendOk(`Sent to ${toEmail.trim()}${cc.length ? ` (cc ${cc.length})` : ""}.`);
      setTimeout(() => { onClose(); }, 1400);
    } catch (err) {
      setSendErr(formatSupabaseError(err));
    } finally {
      setSending(false);
    }
  }

  if (!open) return null;

  const isPm = kind === "pm";

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl my-8">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-bardin-ink">
              {isPm ? `Send ${pmName} PM pay email` : "Send crew pay paid email"}
            </h3>
            <p className="text-[11px] text-slate-500">
              {isPm
                ? "The separate PM payment — tells Julian what to pay Ramiro. Never mixed into the job email."
                : "Summary of crew job payments marked paid — review & edit, then send. PM pay is never on this email."}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl leading-none" aria-label="Close">×</button>
        </div>

        <div className="p-5 space-y-4">
          {/* Date + vendor */}
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-xs text-slate-600">
              <span className="block mb-1">Payments paid on</span>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="text-sm border border-slate-300 rounded px-2 py-1"
              />
            </label>
            {vendors.length > 1 && (
              <label className="text-xs text-slate-600">
                <span className="block mb-1">Vendor</span>
                <select
                  value={vendorId ?? ""}
                  onChange={(e) => setVendorId(e.target.value)}
                  className="text-sm border border-slate-300 rounded px-2 py-1"
                >
                  {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </label>
            )}
          </div>

          {loadErr && <div className="bg-red-50 text-red-700 text-xs p-2 rounded">⚠ {loadErr}</div>}

          {/* Rows list */}
          {rows === null ? (
            <div className="text-sm text-slate-500">Loading paid payments…</div>
          ) : kindRows.length === 0 ? (
            <div className="text-sm text-slate-500 bg-slate-50 rounded p-3">
              {isPm
                ? `No ${pmName} PM pay marked paid on this date${selectedVendor ? ` for ${selectedVendor.name}` : ""}.`
                : `No crew job payments marked paid on this date${selectedVendor ? ` for ${selectedVendor.name}` : ""}.`}
              {" "}You can still add manual lines below and send.
              {!isPm && pmRowCount > 0 && (
                <span className="block mt-1 text-[11px] text-amber-700">
                  {pmRowCount} PM pay row{pmRowCount === 1 ? "" : "s"} paid this day {pmRowCount === 1 ? "is" : "are"} on the {pmName} PM pay email instead — send it from the PM Pay pill.
                </span>
              )}
            </div>
          ) : (
            <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
              {kindRows.map((r) => (
                <label key={r.id} className={`flex items-center gap-3 px-3 py-2 cursor-pointer ${isPm ? "bg-amber-50" : ""}`}>
                  <input
                    type="checkbox"
                    checked={!!include[r.id]}
                    onChange={(e) => setInclude((p) => ({ ...p, [r.id]: e.target.checked }))}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">
                      {isPm
                        ? (r.additional_work_description ?? `${pmName} — PM pay`)
                        : (r.homeowner_name ?? "(no name)")}
                      {!isPm && <span className="text-slate-400 font-normal"> · {r.squares} sq</span>}
                    </div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {isPm
                        ? (r.deal_id ? [r.homeowner_name, r.property_street, r.property_city].filter(Boolean).join(", ") : "Not job-specific")
                        : ([r.property_street, r.property_city].filter(Boolean).join(", ") || "—")}
                      {!isPm && r.additional_work_description ? ` · ${r.additional_work_description}` : ""}
                    </div>
                  </div>
                  <div className="text-sm font-semibold whitespace-nowrap">{fmtUSD(r.total)}</div>
                </label>
              ))}
            </div>
          )}

          {!isPm && kindRows.length > 0 && pmRowCount > 0 && (
            <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
              {pmRowCount} {pmName} PM pay row{pmRowCount === 1 ? "" : "s"} paid this day {pmRowCount === 1 ? "is" : "are"} left off this email on purpose — send {pmRowCount === 1 ? "it" : "them"} from the PM Pay pill.
            </div>
          )}

          {/* Extra / manual lines */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold text-slate-700">Extra lines (highlighted in email)</span>
              <button onClick={addExtra} className="text-[11px] text-bardin-orange hover:underline">+ add line</button>
            </div>
            <p className="text-[11px] text-slate-500 mb-2">
              {isPm
                ? `For one-off ${pmName} amounts not already a row above. Negative = withheld.`
                : `For adjustments on top of the jobs — e.g. a loan repayment withheld (negative). ${pmName}'s pay does not go here; it has its own email on the PM Pay pill.`}
            </p>
            <div className="space-y-2">
              {extraLines.map((e, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder={isPm ? `Label (e.g. ${pmName} — one-off)` : "Label (e.g. Loan repayment withheld)"}
                    value={e.label}
                    onChange={(ev) => setExtra(i, { label: ev.target.value })}
                    className="text-xs border border-slate-300 rounded px-2 py-1 flex-1"
                  />
                  <div className="relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">$</span>
                    <input
                      type="number"
                      inputMode="decimal"
                      placeholder="0.00"
                      value={e.amount}
                      onChange={(ev) => setExtra(i, { amount: ev.target.value })}
                      className="text-xs border border-slate-300 rounded pl-5 pr-2 py-1 w-28"
                    />
                  </div>
                  <button onClick={() => removeExtra(i)} className="text-slate-400 hover:text-red-600 text-sm" aria-label="Remove line">×</button>
                </div>
              ))}
            </div>
          </div>

          {/* Totals preview */}
          <div className="bg-slate-50 rounded-lg p-3 text-sm">
            <div className="flex justify-between"><span className="text-slate-600">{isPm ? "PM pay subtotal" : "Jobs subtotal"}</span><span className="font-medium">{fmtUSD(jobsSubtotal)}</span></div>
            {extrasTotal !== 0 && (
              <div className={`flex justify-between ${extrasTotal < 0 ? "text-rose-700" : "text-amber-800"}`}><span>{extrasTotal < 0 ? "Deductions" : "Extra lines"}</span><span className="font-semibold">{fmtUSD(extrasTotal)}</span></div>
            )}
            <div className="flex justify-between border-t border-slate-200 mt-1 pt-1"><span className="font-semibold">Total sent</span><span className="font-extrabold text-bardin-orange">{fmtUSD(grandTotal)}</span></div>
          </div>

          {/* Recipients */}
          <div className="grid grid-cols-1 gap-2">
            <label className="text-xs text-slate-600">
              <span className="block mb-1">To</span>
              <input type="email" value={toEmail} onChange={(e) => setToEmail(e.target.value)} className="text-sm border border-slate-300 rounded px-2 py-1 w-full" />
            </label>
            <label className="text-xs text-slate-600">
              <span className="block mb-1">Cc (comma-separated)</span>
              <input type="text" value={ccText} onChange={(e) => setCcText(e.target.value)} className="text-sm border border-slate-300 rounded px-2 py-1 w-full" />
            </label>
            <label className="text-xs text-slate-600">
              <span className="block mb-1">Subject</span>
              <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} className="text-sm border border-slate-300 rounded px-2 py-1 w-full" />
            </label>
            <label className="text-xs text-slate-600">
              <span className="block mb-1">Note / greeting</span>
              <textarea value={intro} onChange={(e) => setIntro(e.target.value)} rows={4} className="text-sm border border-slate-300 rounded px-2 py-1 w-full" />
            </label>
          </div>

          {sendErr && <div className="bg-red-50 text-red-700 text-xs p-2 rounded">⚠ {sendErr}</div>}
          {sendOk && <div className="bg-emerald-50 text-emerald-800 text-xs p-2 rounded">✓ {sendOk}</div>}
        </div>

        <div className="px-5 py-4 border-t border-slate-100 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-xs rounded border border-slate-300 text-slate-600 hover:bg-slate-50">Cancel</button>
          <button
            onClick={send}
            disabled={sending}
            className="px-4 py-1.5 text-xs font-medium rounded bg-bardin-orange text-white hover:opacity-90 disabled:opacity-50"
          >
            {sending ? "Sending…" : `Send ${isPm ? "PM pay" : "crew pay"} email · ${fmtUSD(grandTotal)}`}
          </button>
        </div>
      </div>
    </div>
  );
}
